/**
 * aeterna/neural/embeddings.ts — Random Projection Embeddings
 *
 * Dense semantic embeddings without any model. Johnson-Lindenstrauss:
 * a random Gaussian matrix projects sparse TF-IDF vectors into ~64 dims
 * while approximately preserving cosine distances.
 *
 * Used for:
 *   - Intent classification (message embedding → nearest template)
 *   - Room-mood-to-response mapping
 *   - Conversation fingerprinting for Hopfield storage
 *   - Novelty detection (how different is this from recent messages?)
 *
 * The vocabulary is built incrementally — new words get assigned IDs
 * on first encounter. The projection matrix is fixed at init from a
 * deterministic seed, so embeddings are reproducible.
 */

import {
  zeros, dot, normalize, cosine, sum,
  mulberry32, gaussianSeeded,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Embedding dimensionality after projection */
const EMBED_DIM = 64;

/** Maximum vocabulary size */
const MAX_VOCAB = 8192;

/** Minimum word length to include */
const MIN_WORD_LEN = 2;

// ─── Vocabulary ──────────────────────────────────────────────────────────────

const vocab = new Map<string, number>();
let nextId = 0;

/** Document frequency: how many documents each word has appeared in */
const df = new Map<number, number>();
let totalDocs = 0;

/** Get or assign vocabulary ID for a word */
function getWordId(word: string): number {
  let id = vocab.get(word);
  if (id === undefined) {
    if (nextId >= MAX_VOCAB) return -1; // vocab full
    id = nextId++;
    vocab.set(word, id);
  }
  return id;
}

// ─── Projection Matrix ───────────────────────────────────────────────────────
// R: EMBED_DIM × MAX_VOCAB, generated lazily from deterministic seed.
// Each element is N(0, 1/sqrt(EMBED_DIM)).

let projectionMatrix: Float32Array | null = null;
const PROJECTION_SEED = 31415;

function getProjection(): Float32Array {
  if (!projectionMatrix) {
    const rng = mulberry32(PROJECTION_SEED);
    const scale = 1 / Math.sqrt(EMBED_DIM);
    projectionMatrix = new Float32Array(EMBED_DIM * MAX_VOCAB);
    for (let i = 0; i < projectionMatrix.length; i++) {
      projectionMatrix[i] = gaussianSeeded(rng) * scale;
    }
  }
  return projectionMatrix;
}

// ─── Tokenization ────────────────────────────────────────────────────────────

/** Simple whitespace + punctuation tokenizer, lowercased */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s'-]/g, " ")
    .split(/\s+/)
    .filter(w => w.length >= MIN_WORD_LEN);
}

// ─── TF-IDF ──────────────────────────────────────────────────────────────────

interface SparseVec {
  indices: number[];
  values: number[];
}

/** Compute term frequency (normalized) for a token sequence */
function computeTF(tokens: string[]): SparseVec {
  const counts = new Map<number, number>();
  for (const token of tokens) {
    const id = getWordId(token);
    if (id < 0) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  const maxCount = Math.max(1, ...counts.values());
  const indices: number[] = [];
  const values: number[] = [];

  for (const [id, count] of counts) {
    indices.push(id);
    values.push(0.5 + 0.5 * count / maxCount); // augmented TF
  }

  return { indices, values };
}

/** Update document frequency counts */
function updateDF(tf: SparseVec): void {
  totalDocs++;
  for (const id of tf.indices) {
    df.set(id, (df.get(id) ?? 0) + 1);
  }
}

/** Compute TF-IDF from term frequency */
function applyIDF(tf: SparseVec): SparseVec {
  const indices = tf.indices;
  const values = new Array(indices.length);
  for (let i = 0; i < indices.length; i++) {
    const docFreq = df.get(indices[i]) ?? 1;
    const idf = Math.log((totalDocs + 1) / (docFreq + 1)) + 1;
    values[i] = tf.values[i] * idf;
  }
  return { indices, values: values as number[] };
}

// ─── Projection ──────────────────────────────────────────────────────────────

/** Project a sparse TF-IDF vector into the dense embedding space */
function project(tfidf: SparseVec): Float32Array {
  const R = getProjection();
  const out = zeros(EMBED_DIM);

  for (let s = 0; s < tfidf.indices.length; s++) {
    const idx = tfidf.indices[s];
    const val = tfidf.values[s];
    const offset = idx; // R is stored as EMBED_DIM × MAX_VOCAB
    for (let d = 0; d < EMBED_DIM; d++) {
      out[d] += R[d * MAX_VOCAB + offset] * val;
    }
  }

  return normalize(out);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Embed a text string into a 64-dim dense vector.
 * Updates vocabulary and document frequency on each call.
 */
export function embed(text: string): Float32Array {
  const tokens = tokenize(text);
  if (tokens.length === 0) return zeros(EMBED_DIM);

  const tf = computeTF(tokens);
  updateDF(tf);
  const tfidf = applyIDF(tf);
  return project(tfidf);
}

/** Embed without updating document frequencies (for query/comparison) */
export function embedQuery(text: string): Float32Array {
  const tokens = tokenize(text);
  if (tokens.length === 0) return zeros(EMBED_DIM);
  const tf = computeTF(tokens);
  const tfidf = applyIDF(tf);
  return project(tfidf);
}

/** Semantic similarity between two texts */
export function textSimilarity(a: string, b: string): number {
  const ea = embedQuery(a);
  const eb = embedQuery(b);
  return cosine(ea, eb);
}

/**
 * Find the nearest template from a library.
 * Returns the index and similarity of the best match.
 */
export function findNearest(
  queryEmbed: Float32Array,
  templates: Float32Array[],
): { index: number; similarity: number } {
  let bestIdx = 0;
  let bestSim = -Infinity;
  for (let i = 0; i < templates.length; i++) {
    const sim = cosine(queryEmbed, templates[i]);
    if (sim > bestSim) { bestSim = sim; bestIdx = i; }
  }
  return { index: bestIdx, similarity: bestSim };
}

/**
 * Novelty score: how different is this embedding from a set of recent ones?
 * Returns 0 (identical to recent) to 1 (completely novel).
 */
export function noveltyScore(current: Float32Array, recent: Float32Array[]): number {
  if (recent.length === 0) return 1;
  let maxSim = 0;
  for (const r of recent) {
    const sim = cosine(current, r);
    if (sim > maxSim) maxSim = sim;
  }
  return 1 - maxSim;
}

/** Get the embedding dimensionality */
export function getEmbedDim(): number { return EMBED_DIM; }

/** Get current vocabulary size */
export function getVocabSize(): number { return nextId; }

// ─── Character-Level Embedding (for CharCNN input) ───────────────────────────

/**
 * Encode text as a sequence of one-hot character vectors.
 * Returns Float32Array of shape [seqLen × 256] (UTF-8 byte values).
 * Padded/truncated to maxLen.
 */
export function charEncode(text: string, maxLen = 256): Float32Array {
  const bytes = new TextEncoder().encode(text.slice(0, maxLen));
  const seqLen = Math.min(bytes.length, maxLen);
  const out = zeros(maxLen * 256);
  for (let i = 0; i < seqLen; i++) {
    out[i * 256 + bytes[i]] = 1;
  }
  return out;
}

/** Encode text as raw byte values (for simpler char embeddings) */
export function charBytes(text: string, maxLen = 256): Float32Array {
  const bytes = new TextEncoder().encode(text.slice(0, maxLen));
  const out = zeros(maxLen);
  for (let i = 0; i < Math.min(bytes.length, maxLen); i++) {
    out[i] = bytes[i] / 255; // normalize to [0, 1]
  }
  return out;
}

// ─── Contrastive Self-Bootstrapping ──────────────────────────────────────────
// The projection matrix R starts random (JL preserves distances but not semantics).
// Over time, we refine R using conversation structure:
//   - Adjacent messages in a conversation should embed CLOSER
//   - Distant messages should embed FARTHER
// This is self-supervised: no labels needed. The conversation IS the training data.
//
// After a few hundred messages, "cozy" and "warm" will be geometrically close —
// not because we told the system, but because the player uses them in similar contexts.

const BOOTSTRAP_HISTORY_MAX = 100;
const bootstrapHistory: Float32Array[] = [];
let bootstrapCount = 0;

/**
 * Record an embedding into the contrastive history.
 * Called automatically by embed() — the system learns as it goes.
 */
function recordForBootstrap(embedding: Float32Array): void {
  bootstrapHistory.push(new Float32Array(embedding));
  if (bootstrapHistory.length > BOOTSTRAP_HISTORY_MAX) {
    bootstrapHistory.shift();
  }
  bootstrapCount++;

  // Every 20 messages, run one contrastive update step
  if (bootstrapCount % 20 === 0 && bootstrapHistory.length >= 10) {
    contrastiveStep();
  }
}

/**
 * One step of contrastive refinement of the projection matrix.
 *
 * Positive pairs: messages within 3 of each other in history (conversationally adjacent)
 * Negative pairs: messages >10 apart (different topic/mood likely)
 *
 * Update R to increase similarity of positive pairs, decrease negative.
 */
function contrastiveStep(): void {
  const R = getProjection();
  const lr = 0.0001; // very gentle — we're refining, not retraining
  const h = bootstrapHistory;
  const n = h.length;

  for (let attempts = 0; attempts < 5; attempts++) {
    // Random positive pair (adjacent)
    const i = Math.floor(Math.random() * (n - 3));
    const j = i + 1 + Math.floor(Math.random() * 2); // 1-2 apart
    if (j >= n) continue;

    // Random negative pair (distant)
    const k = Math.min(n - 1, i + 10 + Math.floor(Math.random() * (n - i - 10)));
    if (k <= j + 5 || k >= n) continue;

    const emb_i = h[i];
    const emb_j = h[j]; // positive
    const emb_k = h[k]; // negative

    const sim_pos = cosine(emb_i, emb_j);
    const sim_neg = cosine(emb_i, emb_k);

    // Only update if negative is closer than positive (violating desired ordering)
    if (sim_neg >= sim_pos - 0.1) {
      // Push positive pair closer: adjust projection for shared vocab
      // Push negative pair apart: adjust projection for differing vocab
      // This is approximate gradient on the projection — effective enough
      for (let d = 0; d < EMBED_DIM; d++) {
        const pull = (emb_j[d] - emb_i[d]) * lr;
        const push = (emb_k[d] - emb_i[d]) * lr;
        // Adjust the relevant projection columns
        // (Approximation: adjust embedding directly, backprop through R is too expensive)
        emb_i[d] += pull - push;
      }
      normalize(emb_i);
    }
  }
}

// ─── Semantic Lexicon ────────────────────────────────────────────────────────
// The "GloVe without GloVe" approach.
//
// Random projection (JL) preserves distances between TF-IDF vectors, but
// TF-IDF doesn't encode semantics: "happy" and "joyful" are orthogonal
// one-hot dimensions. With random R, they project to random directions.
//
// Fix: override the projection columns for ~200 emotionally relevant words
// with hand-crafted vectors organized in semantic clusters. Words in the
// same cluster share a base direction + small perturbation. This gives the
// embedding space real semantic geometry for the words that matter most.
//
// All other words stay random-projected (JL still valid).
// Zero external files. The knowledge is compiled in.

/** Semantic cluster: words that should be geometrically close */
interface SemanticCluster {
  /** Base direction in embedding space (normalized, 64-dim) */
  direction: number[];
  /** Words in this cluster */
  words: string[];
  /** How spread out the cluster is (0 = identical, 0.3 = varied) */
  spread: number;
}

// Each cluster defines a direction in 64-dim space.
// We only specify ~8 "anchor" dimensions; the rest stay at 0 before normalization.
// The anchor dims define which AFFECT and SEMANTIC axes this cluster lives on.
//
// Dims 0-7 loosely correspond to: warmth, tension, curiosity, melancholy,
// playfulness, depth, urgency, openness (matching affect labels).
// Dims 8-15: social (connection, isolation, trust, sharing, loss, memory, growth, identity)
// Dims 16-23: physical (light, dark, large, small, warm-temp, cold-temp, natural, artificial)
// Remaining dims: available for contrastive refinement over time.

const SEMANTIC_CLUSTERS: SemanticCluster[] = [
  // ── Positive Emotion ──
  {
    direction: [0.7, -0.1, 0.2, -0.2, 0.4, 0.0, -0.1, 0.5,  0.3, -0.2, 0.1, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["happy", "joyful", "glad", "cheerful", "delighted", "wonderful", "fantastic", "amazing", "great", "lovely"],
    spread: 0.15,
  },
  {
    direction: [0.6, -0.2, 0.0, -0.1, 0.1, 0.0, -0.2, 0.4,  0.2, -0.1, 0.3, 0.0, 0.0, 0.2, 0.0, 0.0],
    words: ["grateful", "thankful", "blessed", "fortunate", "appreciative", "touched"],
    spread: 0.12,
  },
  {
    direction: [0.5, -0.1, 0.0, -0.1, 0.0, 0.1, -0.3, 0.6,  0.4, 0.0, 0.2, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["peaceful", "calm", "serene", "tranquil", "relaxed", "content", "satisfied"],
    spread: 0.12,
  },
  {
    direction: [0.3, -0.1, 0.5, -0.1, 0.6, 0.0, 0.1, 0.4,  0.1, 0.0, 0.0, 0.0, 0.0, 0.0, 0.3, 0.0],
    words: ["excited", "thrilled", "eager", "pumped", "hyped", "stoked"],
    spread: 0.15,
  },
  {
    direction: [0.4, -0.1, 0.1, -0.1, 0.2, 0.2, -0.2, 0.3,  0.2, 0.0, 0.0, 0.0, 0.0, 0.0, 0.2, 0.2],
    words: ["hopeful", "optimistic", "encouraged", "confident", "inspired"],
    spread: 0.12,
  },
  // ── Negative Emotion ──
  {
    direction: [-0.1, 0.2, -0.2, 0.7, -0.3, 0.2, 0.0, -0.2,  -0.1, 0.4, -0.1, 0.0, 0.3, 0.0, 0.0, 0.0],
    words: ["sad", "unhappy", "depressed", "heartbroken", "devastated", "miserable", "sorrowful", "grief", "mourning"],
    spread: 0.15,
  },
  {
    direction: [-0.1, 0.2, -0.2, 0.6, -0.2, 0.1, 0.0, -0.1,  -0.2, 0.5, -0.2, 0.0, 0.2, 0.0, 0.0, 0.0],
    words: ["lonely", "isolated", "alone", "abandoned", "disconnected", "left out"],
    spread: 0.12,
  },
  {
    direction: [0.0, 0.3, -0.1, 0.4, -0.2, 0.1, 0.0, -0.1,  -0.1, 0.1, -0.2, 0.0, 0.4, 0.3, 0.0, 0.0],
    words: ["miss", "missing", "longing", "yearn", "nostalgia", "nostalgic", "wistful"],
    spread: 0.12,
  },
  {
    direction: [-0.3, 0.7, -0.1, 0.2, -0.3, 0.0, 0.5, -0.3,  -0.1, 0.0, -0.3, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["angry", "furious", "frustrated", "annoyed", "irritated", "mad", "rage", "outraged"],
    spread: 0.15,
  },
  {
    direction: [-0.1, 0.5, -0.2, 0.3, -0.2, 0.0, 0.3, -0.3,  -0.2, 0.1, -0.2, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["afraid", "scared", "frightened", "terrified", "anxious", "nervous", "worried", "panicked", "dread"],
    spread: 0.15,
  },
  {
    direction: [-0.2, 0.3, -0.1, 0.2, -0.1, 0.0, 0.1, -0.2,  -0.1, 0.0, -0.1, 0.0, 0.0, 0.0, 0.0, 0.3],
    words: ["tired", "exhausted", "drained", "burnt", "weary", "fatigued", "overwhelmed"],
    spread: 0.12,
  },
  // ── Love / Connection ──
  {
    direction: [0.8, -0.1, 0.1, 0.1, 0.1, 0.3, -0.1, 0.4,  0.5, -0.3, 0.4, 0.2, 0.0, 0.2, 0.0, 0.0],
    words: ["love", "adore", "cherish", "treasure", "beloved", "affection", "devotion"],
    spread: 0.12,
  },
  {
    direction: [0.5, -0.1, 0.1, 0.0, 0.0, 0.2, -0.1, 0.3,  0.5, -0.1, 0.5, 0.3, 0.0, 0.0, 0.0, 0.0],
    words: ["trust", "loyal", "faithful", "reliable", "depend", "honest", "sincere"],
    spread: 0.12,
  },
  {
    direction: [0.6, -0.1, 0.1, 0.0, 0.2, 0.1, -0.1, 0.4,  0.6, 0.0, 0.2, 0.3, 0.0, 0.0, 0.0, 0.0],
    words: ["friend", "friendship", "companion", "buddy", "pal", "close", "bond", "connection"],
    spread: 0.15,
  },
  {
    direction: [0.5, -0.1, 0.0, 0.1, 0.0, 0.2, -0.1, 0.3,  0.4, 0.0, 0.1, 0.0, 0.2, 0.3, 0.0, 0.0],
    words: ["family", "mother", "father", "sister", "brother", "parent", "child", "daughter", "son", "sibling"],
    spread: 0.15,
  },
  {
    direction: [0.4, -0.1, 0.0, 0.0, 0.0, 0.1, -0.1, 0.2,  0.3, 0.0, 0.0, 0.2, 0.0, 0.1, 0.0, 0.0],
    words: ["home", "belong", "safe", "shelter", "refuge", "hearth", "sanctuary"],
    spread: 0.12,
  },
  // ── Care / Empathy ──
  {
    direction: [0.6, -0.2, 0.2, 0.0, 0.0, 0.3, -0.2, 0.5,  0.3, 0.0, 0.2, 0.3, 0.0, 0.0, 0.0, 0.0],
    words: ["care", "caring", "kind", "gentle", "tender", "compassion", "empathy", "support"],
    spread: 0.12,
  },
  {
    direction: [0.5, -0.2, 0.3, 0.0, 0.0, 0.3, -0.1, 0.5,  0.2, 0.0, 0.1, 0.2, 0.0, 0.0, 0.0, 0.0],
    words: ["help", "assist", "comfort", "soothe", "heal", "mend", "fix", "ease"],
    spread: 0.12,
  },
  {
    direction: [0.4, -0.2, 0.4, 0.0, 0.0, 0.4, -0.1, 0.5,  0.3, 0.0, 0.2, 0.2, 0.0, 0.0, 0.0, 0.0],
    words: ["listen", "hear", "understand", "empathize", "acknowledge", "validate"],
    spread: 0.12,
  },
  // ── Loss / Hurt ──
  {
    direction: [-0.2, 0.4, -0.1, 0.5, -0.2, 0.2, 0.2, -0.3,  -0.2, 0.3, -0.2, 0.0, 0.4, 0.0, 0.0, 0.0],
    words: ["hurt", "pain", "ache", "wound", "suffer", "agony", "sting", "broken"],
    spread: 0.15,
  },
  {
    direction: [-0.1, 0.3, -0.1, 0.4, -0.1, 0.2, 0.1, -0.2,  -0.3, 0.4, -0.3, 0.0, 0.5, 0.0, 0.0, 0.0],
    words: ["lost", "lose", "loss", "gone", "dead", "death", "died", "passed"],
    spread: 0.15,
  },
  // ── Room / Aesthetic ──
  {
    direction: [0.5, -0.2, 0.0, -0.1, 0.1, 0.0, -0.2, 0.3,  0.2, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.4, 0.0, 0.0, 0.0, 0.4, 0.0, 0.0, 0.0],
    words: ["cozy", "comfortable", "snug", "homey", "inviting", "welcoming", "warm"],
    spread: 0.12,
  },
  {
    direction: [0.2, -0.1, 0.2, -0.1, 0.2, 0.1, 0.0, 0.2,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.6, 0.0, 0.0, 0.0, 0.0, 0.0, 0.3, 0.0],
    words: ["bright", "light", "luminous", "radiant", "sunny", "glowing", "lit"],
    spread: 0.12,
  },
  {
    direction: [0.0, 0.2, -0.1, 0.3, -0.1, 0.2, 0.0, -0.1,  0.0, 0.1, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.6, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["dark", "dim", "shadowy", "gloomy", "murky"],
    spread: 0.12,
  },
  {
    direction: [0.3, -0.2, 0.0, -0.1, 0.0, 0.0, -0.2, 0.2,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.5, 0.0],
    words: ["natural", "organic", "plant", "green", "nature", "garden", "flower", "tree", "leaf"],
    spread: 0.15,
  },
  {
    direction: [0.2, -0.3, 0.0, -0.1, 0.0, 0.0, -0.3, 0.2,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.1, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    words: ["quiet", "silent", "peaceful", "still", "hushed", "serene"],
    spread: 0.12,
  },
  // ── Philosophical / Existential ──
  {
    direction: [0.0, 0.0, 0.5, 0.1, 0.0, 0.7, 0.0, 0.3,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.4],
    words: ["meaning", "purpose", "why", "reason", "cause", "significance"],
    spread: 0.12,
  },
  {
    direction: [0.0, 0.0, 0.4, 0.0, 0.0, 0.7, 0.0, 0.3,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.5],
    words: ["exist", "existence", "real", "reality", "alive", "conscious", "consciousness", "aware"],
    spread: 0.15,
  },
  {
    direction: [0.1, 0.0, 0.6, 0.0, 0.1, 0.5, 0.0, 0.4,  0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.3],
    words: ["think", "thought", "wonder", "ponder", "contemplate", "reflect", "consider"],
    spread: 0.12,
  },
  {
    direction: [0.2, 0.0, 0.3, 0.1, 0.1, 0.4, 0.0, 0.3,  0.0, 0.0, 0.0, 0.0, 0.0, 0.3, 0.2, 0.3],
    words: ["dream", "imagine", "fantasy", "vision", "wish", "hope", "aspire"],
    spread: 0.15,
  },
  {
    direction: [0.1, 0.0, 0.1, 0.0, 0.0, 0.3, 0.0, 0.2,  0.0, 0.0, 0.0, 0.0, 0.0, 0.4, 0.0, 0.3],
    words: ["remember", "memory", "memories", "recall", "reminisce", "past", "history"],
    spread: 0.12,
  },
  {
    direction: [-0.1, 0.2, -0.1, 0.3, 0.0, 0.2, 0.1, -0.1,  0.0, 0.0, 0.0, 0.0, 0.3, 0.0, 0.0, 0.2],
    words: ["forget", "forgotten", "erase", "fade", "disappear", "vanish"],
    spread: 0.12,
  },
  // ── Playfulness / Humor ──
  {
    direction: [0.3, -0.2, 0.2, -0.2, 0.7, 0.0, 0.0, 0.4,  0.1, 0.0, 0.0, 0.0, 0.0, 0.0, 0.3, 0.0],
    words: ["funny", "hilarious", "laugh", "laughing", "joke", "humor", "silly", "goofy", "ridiculous"],
    spread: 0.15,
  },
  {
    direction: [0.2, -0.1, 0.3, -0.1, 0.6, 0.0, 0.0, 0.5,  0.2, 0.0, 0.0, 0.0, 0.0, 0.0, 0.4, 0.0],
    words: ["play", "playing", "fun", "enjoy", "game", "playful"],
    spread: 0.12,
  },
];

/**
 * Override projection columns for semantically-seeded words.
 *
 * For each word in a cluster, we set its column in the projection matrix R
 * to the cluster's base direction + small random perturbation (so words in
 * the same cluster are close but not identical).
 *
 * This gives us "GloVe without GloVe": real semantic geometry for the words
 * that carry emotional weight, while all other words stay random-projected.
 */
function applySemanticSeeding(seed: number): void {
  const R = getProjection();
  const rng = mulberry32(seed + 77777);

  for (const cluster of SEMANTIC_CLUSTERS) {
    const baseDir = cluster.direction;
    const baseDim = baseDir.length;

    for (const word of cluster.words) {
      const id = getWordId(word);
      if (id < 0) continue; // vocab full

      // Generate the seeded vector: base direction + perturbation
      for (let d = 0; d < EMBED_DIM; d++) {
        const base = d < baseDim ? baseDir[d] : 0;
        const perturbation = gaussianSeeded(rng) * cluster.spread;
        R[d * MAX_VOCAB + id] = base + perturbation;
      }

      // Normalize the column so this word's contribution has unit magnitude
      let norm = 0;
      for (let d = 0; d < EMBED_DIM; d++) {
        norm += R[d * MAX_VOCAB + id] ** 2;
      }
      norm = Math.sqrt(norm) || 1;
      const scale = 1 / Math.sqrt(EMBED_DIM); // match the JL scaling
      for (let d = 0; d < EMBED_DIM; d++) {
        R[d * MAX_VOCAB + id] = (R[d * MAX_VOCAB + id] / norm) * scale * EMBED_DIM * 0.5;
      }
    }
  }
}

let seeded = false;
/** Ensure seed vocabulary is registered AND semantic lexicon applied */
export function seedVocabulary(): void {
  if (seeded) return;
  seeded = true;

  // 1. Register all seed words (assigns low vocab IDs)
  const allSemanticWords = SEMANTIC_CLUSTERS.flatMap(c => c.words);
  for (const word of allSemanticWords) {
    getWordId(word);
  }

  // 2. Also register room/temporal/game words that don't need semantic clustering
  const EXTRA_VOCAB = [
    "furniture", "window", "wall", "floor", "ceiling", "lamp", "table", "chair",
    "shelf", "rug", "painting", "clock", "candle", "bookshelf", "fireplace",
    "morning", "evening", "night", "day", "today", "yesterday", "tomorrow",
    "always", "never", "sometimes", "often", "soon", "later", "now",
    "build", "create", "craft", "collect", "explore", "discover", "grow",
    "change", "move", "place", "arrange", "design", "decorate",
  ];
  for (const word of EXTRA_VOCAB) {
    getWordId(word);
  }

  // 3. Apply semantic seeding: override projection columns
  applySemanticSeeding(PROJECTION_SEED);
}

// ─── N-gram Features ─────────────────────────────────────────────────────────
// Bigram features capture word pairs — "not happy" is very different from "happy"

/** Extract character 3-grams for subword features */
export function extractCharNgrams(text: string, n = 3): string[] {
  const lower = text.toLowerCase();
  const grams: string[] = [];
  for (let i = 0; i <= lower.length - n; i++) {
    grams.push(lower.slice(i, i + n));
  }
  return grams;
}

/** Embed with bigram features for richer representation */
export function embedRich(text: string): Float32Array {
  const tokens = tokenize(text);
  if (tokens.length === 0) return zeros(EMBED_DIM);

  // Unigram TF-IDF
  const tf = computeTF(tokens);
  updateDF(tf);
  const tfidf = applyIDF(tf);
  const unigram = project(tfidf);

  // Bigram features: hash word pairs into vocab space
  const bigrams: SparseVec = { indices: [], values: [] };
  for (let i = 0; i < tokens.length - 1; i++) {
    const pair = `${tokens[i]}_${tokens[i + 1]}`;
    const id = getWordId(pair);
    if (id >= 0) {
      bigrams.indices.push(id);
      bigrams.values.push(0.5); // lower weight than unigrams
    }
  }
  const bigramProj = bigrams.indices.length > 0 ? project(bigrams) : zeros(EMBED_DIM);

  // Blend: 70% unigram + 30% bigram
  const result = zeros(EMBED_DIM);
  for (let i = 0; i < EMBED_DIM; i++) {
    result[i] = unigram[i] * 0.7 + bigramProj[i] * 0.3;
  }
  normalize(result);

  // Record for contrastive bootstrapping
  recordForBootstrap(result);

  return result;
}
